import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'Utils/queryClient';
import { Http } from 'Utils/Http';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';

const AI_NEW_THREAD_CHAT = 'AI_NEW_THREAD_CHAT';
const AI_GET_THREADS = 'AI_GET_THREADS';
const AI_GET_MESSAGES = 'AI_GET_MESSAGES';
const AI_SEND_MESSAGE_TO_THREAD = 'AI_SEND_MESSAGE_TO_THREAD';
const AI_SEND_FEEDBACK = 'AI_SEND_FEEDBACK';

const appAiApiUrl = process.env.REACT_APP_AI_API_URL || '';

export interface NewThreadChatResponse {
  threadId: string;
  response: string;
  messageId: string;
}

export interface StreamMessageResponse {
  threadId: string;
  messageId: string;
  content: string;
}

export const useSendFeedback = () => {
  return useMutation({
    mutationKey: [AI_SEND_FEEDBACK],
    mutationFn: async (body: {
      comments: string;
      threadId: string;
      messageId: string;
    }): Promise<{ success: boolean }> => {
      const authorization = await Http.getValidAccessToken();
      const response = await fetch(`${appAiApiUrl}/Feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authorization}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Comments: body.comments,
          ThreadId: body.threadId,
          MessageId: body.messageId
        })
      });

      if (!response.ok) {
        return { success: false };
      }

      return { success: true };
    }
  });
};

export const useSendNewThreadChat = (constructionSiteId: number, assistant?: string) => {
  return useMutation({
    mutationKey: [AI_NEW_THREAD_CHAT],
    mutationFn: async (message: string): Promise<NewThreadChatResponse> => {
      const authorization = await Http.getValidAccessToken();
      const headers: HeadersInit = {
        Authorization: `Bearer ${authorization}`,
        'Content-Type': 'application/json'
      };
      if (assistant) {
        headers['Assistant'] = assistant;
      }
      const response = await fetch(`${appAiApiUrl}/Chat/${constructionSiteId}/new`, {
        method: 'POST',
        headers,
        body: JSON.stringify(message)
      });

      if (!response.ok) {
        return {
          response: 'Algo deu errado, tente novamente mais tarde.',
          threadId: '',
          messageId: ''
        };
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_GET_THREADS] });
    }
  });
};

export const useSendMessageToThread = (
  constructionSiteId: number,
  threadId?: string,
  onStreamData?: (token: string) => void,
  assistant?: string
) => {
  const shouldStream = true;
  return useMutation({
    mutationKey: [AI_SEND_MESSAGE_TO_THREAD],
    mutationFn: async (message: string): Promise<StreamMessageResponse> => {
      const authorization = await Http.getValidAccessToken();
      const headers: HeadersInit = {
        Authorization: `Bearer ${authorization}`,
        'Content-Type': 'application/json',
        Accept: 'text/plain'
      };

      if (assistant) {
        headers['Assistant'] = assistant;
      }

      return new Promise((resolve, reject) => {
        fetch(
          `${appAiApiUrl}/Chat/${constructionSiteId}/thread/${threadId}${
            shouldStream ? '/stream' : ''
          }`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(message),
            cache: 'no-store'
          }
        )
          .then(async (response) => {
            if (!response.ok) {
              reject(new Error('Falha ao enviar mensagem para a thread'));
              return;
            }

            if (!shouldStream) {
              const data = await response.json();

              return resolve({
                threadId: data.threadId,
                messageId: data.messageId,
                content: data.response
              });
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder('utf-8');
            const startTime = new Date().getTime();
            const timeout = 60000; // read for 60 seconds
            let fullContent = '';

            if (reader) {
              try {
                while (new Date().getTime() - startTime < timeout) {
                  const { done, value } = await reader.read();
                  if (done) break;

                  const chunk = decoder.decode(value, { stream: true });

                  if (chunk && chunk.trim()) {
                    fullContent += chunk;
                    if (onStreamData) {
                      onStreamData(chunk);
                    }
                  }
                }

                const remaining = decoder.decode();
                if (remaining && remaining.trim()) {
                  fullContent += remaining;

                  if (onStreamData) {
                    onStreamData(remaining);
                  }
                }
              } finally {
                reader.releaseLock();
              }
            }

            resolve({
              threadId: threadId || '',
              messageId: `msg_${Date.now()}`,
              content: fullContent
            });
          })
          .catch(reject);
      });
    }
  });
};

export interface Thread {
  threadId: string;
  userId: string;
  constructionSiteId: number;
  resume: string;
  createdAt: string;
}

export const useGetThreads = (constructionSiteId: number) => {
  return useQuery<Thread[]>({
    queryKey: [AI_GET_THREADS],
    queryFn: async (): Promise<Thread[]> => {
      const authorization = await Http.getValidAccessToken();
      const response = await fetch(`${appAiApiUrl}/Chat/${constructionSiteId}/thread`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authorization}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    }
  });
};

export enum MessageRole {
  USER = 0,
  BOT = 1
}

export interface AIChatMessage {
  messageId: string;
  role: MessageRole;
  content: string;
}

export interface AIChatMessageResponse {
  threadId: string;
  userId: string;
  constructionSiteId: number;
  resume: string;
  createdAt: string;
  messages: AIChatMessage[];
}

export const useGetMessages = (constructionSiteId: number, threadId?: string) => {
  return useQuery<AIChatMessageResponse | null>({
    queryKey: [AI_GET_MESSAGES, threadId],
    queryFn: async (): Promise<AIChatMessageResponse | null> => {
      if (!threadId) {
        return null;
      }

      const authorization = await Http.getValidAccessToken();
      const response = await fetch(
        `${appAiApiUrl}/Chat/${constructionSiteId}/thread/${threadId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authorization}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    }
  });
};
